import { BoostPopover } from '@/components/BoostPopover'
import { DiscordButton } from '@/components/Buttons/DiscordButton'
import { ProButton } from '@/components/Buttons/ProButton'
import { LanguagesPopover } from '@/components/LanguagesPopover'
import { MuseLogo } from '@/components/MuseLogo'
import { NotificationsPopover } from '@/components/NotificationsPopover'
import { UserProfilePopover } from '@/components/UserProfilePopover'
import BellIcon from '@/CustomIcons/BellIcon'
import BoostIcon from '@/CustomIcons/BoostIcon'
import HomeIcon from '@/CustomIcons/HomeIcon'
import MenuIcon from '@/CustomIcons/MenuIcon'
import UserIcon from '@/CustomIcons/UserIcon'
import MessageIcon from '@/CustomIcons/MessagesIcon'
import { useIsProUser } from '@/services/useIsProUser'
import { Button } from '@/UIComponents2/Button'
import { CircleIconButton } from '@/UIComponents2/CircleIconButton'
import { useUserContext } from '@/UserContext'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import CommunityIcon from '@/CustomIcons/CommunityIcon'
import { cn } from '@/utlis/cn'
import { DropdownMenu } from 'radix-ui'
import { PortalContext } from '@/UIComponents2/PortalContext'
import { useContext } from 'react'
import ClearIcon from '@/CustomIcons/ClearIcon'
import LanguageIcon from '@/CustomIcons/LanguageIcon'
import MobileSessionsMenu from './MobileSessionsMenu'
import Tooltip from '@/UIComponents2/Tooltip'
import useAnalytics from '@/services/useAnalytics'
import { AnalyticsEvents } from '@/services/AnalyticsService'

type TopBarProps = {
    className?: string
}
export function TopBar({ className }: TopBarProps) {
    const { t } = useTranslation()
    const isProUser = useIsProUser()
    const [userState] = useUserContext()

    return (
        <div className={cn('flex justify-between items-center', className)}>
            <div className="flex items-center gap-4 md:gap-[60px] w-full md:w-auto">
                <MuseLogo className="w-[85px]" />
                <TopNav />
            </div>
            <div className="hidden md:flex items-center gap-[4px] pr-[40px] min-[1440px]:pr-3">
                {!isProUser && <ProButton className="hidden md:flex">{t('Upgrade to Pro')}</ProButton>}
                <LanguagesPopover>
                    <CircleIconButton>
                        <LanguageIcon style={{ fontSize: '1.3rem' }} />
                    </CircleIconButton>
                </LanguagesPopover>
                <DiscordButton className="hidden lg:flex" />
                {!isProUser && (
                    <BoostPopover className="hidden lg:flex">
                        <CircleIconButton>
                            <BoostIcon style={{ fontSize: '1.3rem' }} />
                            {userState.profile?.boostsRemaining ?? 0}
                        </CircleIconButton>
                    </BoostPopover>
                )}
                <NotificationsPopover>
                    <CircleIconButton>
                        <BellIcon style={{ fontSize: '1.3rem' }} />
                    </CircleIconButton>
                </NotificationsPopover>
                <UserProfilePopover>
                    <CircleIconButton>
                        <UserIcon style={{ fontSize: '1.3rem' }} />
                    </CircleIconButton>
                </UserProfilePopover>
            </div>
        </div>
    )
}

function useNavLinks() {
    const { t } = useTranslation()
    const location = useLocation()
    const [_, userDispatch] = useUserContext()

    const links = [
        {
            label: t('Home'),
            path: '/',
            icon: HomeIcon,
            active: location.pathname === '/',
            disabled: false,
        },
        {
            label: t('Messages'),
            path: '/pages/main-community-page',
            onClick: () => {
                userDispatch({
                    type: 'update_community_state',
                    value: { communityPageCurrentTab: 'messages' },
                })
            },
            icon: MessageIcon,
            active: location.pathname === '/pages/main-community-page',
            disabled: true,
            tooltip: t('Coming soon!'),
        },
        {
            label: t('Community'),
            path: '/pages/main-community-page',
            onClick: () => {
                userDispatch({
                    type: 'update_community_state',
                    value: { communityPageCurrentTab: 'collaborate' },
                })
            },
            icon: CommunityIcon,
            active: location.pathname === '/pages/main-community-page',
            disabled: true,
            tooltip: t('Coming soon!'),
        },
    ]

    return links
}

function TopNav() {
    const history = useHistory()
    const links = useNavLinks()
    const { logUserActivityWithContext } = useAnalytics()

    return (
        <>
            <div className="grow flex md:hidden items-center justify-between">
                <MobileNavMenu />
                <MobileSessionsMenu />
            </div>
            <div className="hidden md:inline-flex justify-center items-center gap-1">
                {links.map((link, i) => (
                    <NavLink
                        key={i}
                        onClick={() => {
                            if (link.disabled) {
                                logUserActivityWithContext(AnalyticsEvents.CLICKED_UNIMPLEMENTED, {
                                    where: 'dashboard-nav',
                                    link: link.path,
                                })
                                return
                            }
                            link.onClick?.()
                            history.push(link.path)
                        }}
                        active={link.active}
                        disabled={link.disabled}
                        tooltip={link.tooltip}
                    >
                        <link.icon style={{ fontSize: '1.3rem' }} />
                        {link.label}
                    </NavLink>
                ))}
            </div>
        </>
    )
}

type NavLinkProps = {
    children: React.ReactNode
    active?: boolean
    onClick?: () => void
    disabled?: boolean
    tooltip?: string
}
function NavLink({ children, active, onClick, disabled, tooltip }: NavLinkProps) {
    const layout = 'flex h-[40px] pl-[12px] pr-[18px] justify-center items-center gap-[8px]'
    const border = active ? 'rounded-[70px]' : 'rounded-[70px] border-muse-toplit'
    const bg = active
        ? 'bg-[radial-gradient(circle_at_0.42%_9.62%,#FCE640_0%,#F49629_77.07%,#F17E22_100%)] hover:bg-[radial-gradient(ellipse_140%_140%_at_0.42%_9.62%,#FCE640_0%,#F49629_40%,#F17E22_50%)]'
        : 'bg-white/10 hover:bg-white/7'

    return (
        <Tooltip text={tooltip}>
            <a
                tabIndex={0}
                className={cn(
                    layout,
                    border,
                    bg,
                    active ? 'text-black' : 'font-light',
                    'cursor-pointer',
                    disabled && 'cursor-not-allowed'
                )}
                onClick={onClick}
            >
                {children}
            </a>
        </Tooltip>
    )
}

function MobileNavMenu() {
    const { t } = useTranslation()
    const history = useHistory()
    const isProUser = useIsProUser()
    const [userState] = useUserContext()
    const portalContainer = useContext(PortalContext)
    const links = useNavLinks()

    const linkClasses =
        't-lg w-full px-6 py-3 focus:outline-none border-b-1 border-b-white/10 last:border-none first:rounded-t-[28px]'
    const inactiveLinkClasses = 'hover:bg-white/10 transition-colors duration-250'
    const activeLinkClasses = 'text-black t-lg-medium bg-muse-primary-gradient'
    const circleButtonClasses = 'backdrop-blur-none bg-black/60'

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild className="group">
                <Button>
                    <MenuIcon className="group-data-[state=open]:hidden" />
                    <div className="hidden group-data-[state=open]:block">
                        <ClearIcon />
                    </div>
                </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal container={portalContainer}>
                <DropdownMenu.Content
                    sideOffset={10}
                    side="bottom"
                    className="dropdown-menu-content md:hidden w-screen bg-neutral-700/70 backdrop-blur-[50px] z-30 rounded-b-[28px] rounded-t-[28px]"
                >
                    {links.map((link, i) => (
                        <DropdownMenu.Item
                            key={i}
                            className={cn(linkClasses, link.active ? activeLinkClasses : inactiveLinkClasses)}
                            asChild
                        >
                            <Button
                                className="text-left"
                                onClick={() => {
                                    link.onClick?.()
                                    history.push(link.path)
                                }}
                            >
                                <link.icon style={{ fontSize: '1.3rem' }} /> {link.label}
                            </Button>
                        </DropdownMenu.Item>
                    ))}
                    <DropdownMenu.Label className="h-[30px]"></DropdownMenu.Label>
                    <div className="p-4 flex gap-2">
                        {!isProUser && (
                            <DropdownMenu.Item asChild>
                                <ProButton className={circleButtonClasses}>{t('Upgrade to Pro')}</ProButton>
                            </DropdownMenu.Item>
                        )}
                        <LanguagesPopover>
                            <CircleIconButton className={circleButtonClasses}>
                                <LanguageIcon style={{ fontSize: '1.3rem' }} />
                            </CircleIconButton>
                        </LanguagesPopover>
                        <DiscordButton className={circleButtonClasses} />
                        {!isProUser && (
                            <BoostPopover className="hidden lg:flex">
                                <CircleIconButton className={circleButtonClasses}>
                                    <BoostIcon style={{ fontSize: '1.3rem' }} />
                                    {userState.profile?.boostsRemaining ?? 0}
                                </CircleIconButton>
                            </BoostPopover>
                        )}
                        <NotificationsPopover>
                            <CircleIconButton className={circleButtonClasses}>
                                <BellIcon style={{ fontSize: '1.3rem' }} />
                            </CircleIconButton>
                        </NotificationsPopover>
                        <UserProfilePopover>
                            <CircleIconButton className={circleButtonClasses}>
                                <UserIcon style={{ fontSize: '1.3rem' }} />
                            </CircleIconButton>
                        </UserProfilePopover>
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    )
}
